import React, { useState } from 'react';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { companyInfo } from '../data/mock';
import { toast } from 'sonner';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    location: '',
    requirement: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, requirement: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
    toast.success('Thank you! We will contact you shortly.');
    
    // Reset form after 3 seconds
    setTimeout(() => {
      setFormData({ name: '', phone: '', location: '', requirement: '', message: '' });
      setIsSubmitted(false);
    }, 3000);
  };

  return (
    <section id="contact" className="section-padding bg-[#FDFBF7]">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <span className="inline-block text-[#B8956B] font-medium text-sm uppercase tracking-[0.2em] mb-4">
            Contact Us
          </span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#252A32] mb-6 heading-display">
            Get a Free <span className="text-[#B8956B] italic">Consultation</span>
          </h2>
          <div className="elegant-divider mx-auto mb-6" />
          <p className="text-[#404752] text-lg">
            Ready to elevate your space? Contact us for a free site consultation and quote.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-14">
          {/* Contact Form */}
          <a href="https://wa.me/918113934720?text=Hello%20Becone%20Elevators,%20I%20would%20like%20to%20enquire%20about%20your%20elevator%20services."
            target="_blank"
            class="whatsapp-contact"
            aria-label="Contact Becone Elevators via WhatsApp">

            <img src="https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg"
                alt="Contact Becone Elevators on WhatsApp"
                width="48"
                height="48">
              </img>

            <span>Contact Becone Elevators</span>
          </a>
          
          {/* Contact Info */}
          <div>
            <div className="space-y-8">
              {/* Phone */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-[#B8956B]/10 flex items-center justify-center flex-shrink-0">
                  <Phone className="w-5 h-5 text-[#B8956B]" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#252A32] mb-2">Call Us</h4>
                  <div className="space-y-1">
                    {companyInfo.phones.map((phone, idx) => (
                      <a 
                        key={idx}
                        href={`tel:${phone}`}
                        className="block text-[#404752] hover:text-[#B8956B] transition-colors"
                      >
                        +91 {phone}
                      </a>
                    ))}
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-[#B8956B]/10 flex items-center justify-center flex-shrink-0">
                  <Mail className="w-5 h-5 text-[#B8956B]" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#252A32] mb-2">Email Us</h4>
                  <a 
                    href={`mailto:${companyInfo.email}`}
                    className="text-[#404752] hover:text-[#B8956B] transition-colors"
                  >
                    {companyInfo.email}
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-[#B8956B]/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="w-5 h-5 text-[#B8956B]" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#252A32] mb-2">Visit Us</h4>
                  <p className="text-[#404752]">
                    {companyInfo.address.line1}<br />
                    {companyInfo.address.line2}<br />
                    {companyInfo.address.district}<br />
                    {companyInfo.address.state}
                  </p>
                </div>
              </div>

              {/* Business Hours */}
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 rounded-2xl bg-[#B8956B]/10 flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-[#B8956B]" />
                </div>
                <div>
                  <h4 className="text-lg font-semibold text-[#252A32] mb-2">Business Hours</h4>
                  <p className="text-[#404752]">
                    Monday - Saturday: 9:00 AM - 6:00 PM<br />
                    Sunday: Closed
                  </p>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="mt-10 rounded-2xl overflow-hidden shadow-lg h-64 border border-gray-100">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3927.9474321661!2d76.2234!3d10.1427!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTDCsDA4JzMzLjciTiA3NsKwMTMnMjQuMiJF!5e0!3m2!1sen!2sin!4v1600000000000!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Becone Elevators Location"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
